<template>
  <NuxtLayout name="default" :show-back-button="false">
    <SofaxBreadcrumb :title="t('contact.title')" :items="[{ name: t('contact.title') }]" />

    <!-- Contact details on the left, the message form in a raised panel on the right -->
    <section class="sofax bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="grid grid-cols-1 items-center gap-14 lg:grid-cols-[minmax(0,0.8fr)_1fr]">
            <ul class="flex flex-col gap-y-[3.75rem]">
              <li v-for="item in infoItems" :key="item.icon" class="flex items-start gap-7">
                <div class="h-[3.75rem] w-auto shrink-0">
                  <img :src="item.icon" alt="" aria-hidden="true" width="65" height="60">
                </div>
                <div class="flex-1">
                  <span class="display-heading display-heading-4 mb-4 block">{{ item.title }}</span>
                  <p>
                    {{ item.text }}
                    <template v-if="item.email">
                      <a :href="`mailto:${item.email}`" class="link">{{ item.email }}</a>
                      <!-- The space is inside the interpolation: the compiler strips
                           leading whitespace in a <template>, gluing the sentence
                           onto the e-mail address. -->
                      <template v-if="item.after">{{ ` ${item.after}` }}</template>
                    </template>
                  </p>
                  <p v-for="line in item.lines" :key="line">{{ line }}</p>
                </div>
              </li>
            </ul>

            <div class="panel">
              <h3 class="mb-8">{{ t('contact.form.title') }}</h3>
              <form class="grid grid-cols-1 gap-y-6" @submit.prevent="handleSubmit">
                <SofaxNotice v-if="error" type="error">{{ error }}</SofaxNotice>
                <SofaxNotice v-if="success" type="success">{{ t('contact.form.success') }}</SofaxNotice>

                <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <SofaxInput
                    id="name"
                    v-model="form.name"
                    :label="t('contact.form.name')"
                    type="text"
                    autocomplete="name"
                    required
                  />
                  <SofaxInput
                    id="email"
                    v-model="form.email"
                    :label="t('contact.form.email')"
                    type="email"
                    autocomplete="email"
                    required
                  />
                </div>

                <SofaxSelect
                  id="subject-select"
                  v-model="selectedSubject"
                  :label="t('contact.form.subject')"
                  required
                  @change="handleSubjectChange"
                >
                  <option value="">{{ t('contact.form.subjects.placeholder') }}</option>
                  <option value="general">{{ t('contact.form.subjects.general') }}</option>
                  <option value="support">{{ t('contact.form.subjects.support') }}</option>
                  <option value="billing">{{ t('contact.form.subjects.billing') }}</option>
                  <option value="feature">{{ t('contact.form.subjects.feature') }}</option>
                  <option value="bug">{{ t('contact.form.subjects.bug') }}</option>
                  <option value="feedback">{{ t('contact.form.subjects.feedback') }}</option>
                  <option value="other">{{ t('contact.form.subjects.other') }}</option>
                </SofaxSelect>

                <!-- Custom subject input - shown when "Other" is selected -->
                <SofaxInput
                  v-if="selectedSubject === 'other'"
                  id="custom-subject"
                  v-model="customSubject"
                  :label="t('contact.form.customSubject')"
                  type="text"
                  :placeholder="t('contact.form.customSubjectPlaceholder')"
                  required
                />

                <SofaxTextarea
                  id="message"
                  v-model="form.message"
                  :label="t('contact.form.message')"
                  :rows="6"
                  required
                />

                <div class="flex justify-start">
                  <SofaxBtn
                    type="submit"
                    variant="accent"
                    :label="isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')"
                    :disabled="isSubmitting"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Full-width map of the registered office, as in the template -->
    <div class="sofax">
      <div class="h-96 w-full overflow-hidden lg:h-[600px]">
        <iframe
          :src="mapSrc"
          :title="t('contact.info.mapTitle')"
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
          class="size-full border-0"
        />
      </div>
    </div>

    <SofaxPageCta />
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n()
const { submitForm, isSubmitting, error, success } = useContactForm()
const user = useSupabaseUser();

// SEO Meta
useSeoMeta({
  title: () => t('seo.contact.title'),
  description: () => t('seo.contact.description'),
  ogTitle: () => t('seo.contact.title'),
  ogDescription: () => t('seo.contact.description'),
})

// JSON-LD Structured Data
const { organizationNode, webSiteNode, contactPageNode, useJsonLdGraph } = useJsonLd()
useJsonLdGraph(() => [organizationNode(), webSiteNode(), contactPageNode()])

const SUPPORT_EMAIL = 'info@inmyresume.com'

/*
 * The three template info items, filled from the existing contactInfo/support
 * copy so the old translations keep being used.
 */
const infoItems = computed(() => [
  {
    icon: '/sofax/contact-icon-1.svg',
    title: t('contact.contactInfo.title'),
    text: t('contact.contactInfo.description'),
    lines: [
      t('contact.contactInfo.name'),
      t('contact.contactInfo.company'),
      `${t('contact.contactInfo.addressLabel')}: ${t('contact.contactInfo.addressValue')}`,
      t('contact.contactInfo.ico'),
      t('contact.contactInfo.register'),
    ],
  },
  {
    icon: '/sofax/contact-icon-2.svg',
    title: t('contact.support.title'),
    text: t('contact.support.description'),
    lines: [t('contact.support.responseTime')],
  },
  {
    icon: '/sofax/contact-icon-3.svg',
    title: t('contact.info.emailTitle'),
    text: t('contact.info.emailText'),
    email: SUPPORT_EMAIL,
    after: t('contact.info.emailAfter'),
    lines: [] as string[],
  },
])

// Google Maps embed of the registered address; no API key is needed for this endpoint.
const mapSrc = computed(() => {
  const address = encodeURIComponent(t('contact.contactInfo.addressValue'))
  return `https://maps.google.com/maps?q=${address}&t=&z=14&ie=UTF8&iwloc=B&output=embed`
})

const form = ref({
  name: '',
  email: '',
  subject: '',
  message: '',
  userId: null as string | null
})

const selectedSubject = ref('')
const customSubject = ref('')

// Subject selection options and their translations
const subjectOptions = computed(() => ({
  general: t('contact.form.subjects.general'),
  support: t('contact.form.subjects.support'),
  billing: t('contact.form.subjects.billing'),
  feature: t('contact.form.subjects.feature'),
  bug: t('contact.form.subjects.bug'),
  feedback: t('contact.form.subjects.feedback'),
  other: t('contact.form.subjects.other')
}))

// Handle subject selection change
function handleSubjectChange() {
  if (selectedSubject.value === 'other') {
    form.value.subject = customSubject.value
  } else if (selectedSubject.value && subjectOptions.value[selectedSubject.value as keyof typeof subjectOptions.value]) {
    form.value.subject = subjectOptions.value[selectedSubject.value as keyof typeof subjectOptions.value]
  } else {
    form.value.subject = ''
  }
}

// Watch for changes in custom subject when "other" is selected
watch(customSubject, (newValue) => {
  if (selectedSubject.value === 'other') {
    form.value.subject = newValue
  }
})

// Nastavíme userId pokud je uživatel přihlášen
watchEffect(() => {
  if (user.value) {
    form.value.userId = user.value.id
  }
})

async function handleSubmit() {
  const result = await submitForm(form.value)
  if (result) {
    form.value = {
      name: '',
      email: '',
      subject: '',
      message: '',
      userId: user.value?.id || null
    }
    selectedSubject.value = ''
    customSubject.value = ''
  }
}

/*
 * `layout: false` + explicit NuxtLayout so the default layout's back button
 * can be switched off; definePageMeta cannot pass layout props.
 */
definePageMeta({
  layout: false
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/kontakt', de: '/kontakt', pl: '/kontakt' } })
</script>
